/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './Calendar';
import { page as SamplePage } from '../../help/sample_pages/CalendarPage';

const CalendarManager: ComponentManagerFn<'Calendar'> = (
  platform: Platform,
) => {
  const callback: () => void = emptyFunction;
  return {
    ...baseComponentManager<'Calendar'>(platform, 'Calendar'),
    type: 'Calendar',
    name: 'Calendar',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerOnEventChange',
          'TriggerOnEventClick',
          'TriggerOnEventCreate',
          'TriggerOnDrop',
          'TriggerOnDateChange',
          'Popover',
          'Box',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Data',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'DataStore',
          'LocalDataStore',
        ],
      },
      fromDateAttribute: {
        label: 'From Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'CALENDAR',
        getAttributeDataTypes: () => ['Date', 'DateTime', 'StringDateTime'],
        preventMultiEdit: true,
        required: true,
      },
      toDateAttribute: {
        label: 'To Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'CALENDAR',
        getAttributeDataTypes: () => ['Date', 'DateTime', 'StringDateTime'],
        preventMultiEdit: true,
        required: true,
      },
      titleAttribute: {
        label: 'Title Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'CALENDAR',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
        required: true,
      },
      allDayAttribute: {
        label: 'All Day Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'CALENDAR',
        getAttributeDataTypes: () => ['Boolean'],
        preventMultiEdit: true,
        required: true,
        helpText: 'Must be a boolean attribute',
      },
      allDayText: {
        label: 'All Day Label Text',
        type: 'string',
        category: 'Data',
        helpText:
          'This text will be displayed for all-day events in the calendar.',
      },
      hideAllDaySlot: {
        label: 'Hide All Day Slot',
        type: 'boolean',
        category: 'Data',
      },
      hideTimeSlot: {
        label: 'Hide Time Slot',
        type: 'boolean',
        category: 'Data',
      },
      showOnlyAllDaySlot: {
        label: 'Show Only All Day Slot',
        type: 'boolean',
        category: 'Data',
      },
      showYearMonthPicker: {
        label: 'Show Year and Month Picker',
        type: 'boolean',
        category: 'Data',
      },
      createEventOnClick: {
        label: 'Create Event On Click',
        type: 'boolean',
        category: 'Data',
      },
      month: {
        label: 'Month',
        type: 'boolean',
        category: 'Data',
      },
      week: {
        label: 'Week',
        type: 'boolean',
        category: 'Data',
      },
      day: {
        label: 'Day',
        type: 'boolean',
        category: 'Data',
      },
      list: {
        label: 'List',
        type: 'boolean',
        category: 'Data',
      },
      today: {
        label: 'Today',
        type: 'boolean',
        category: 'Data',
      },
      showPagination: {
        label: 'Show Pagination',
        type: 'boolean',
        category: 'Data',
      },
      disablePastNavigation: {
        label: 'Disable Past Navigation',
        type: 'boolean',
        category: 'Data',
        helpText: 'Enable this to disable navigation to Past Month or Week.',
      },
      renderCount: {
        label: 'Render Count',
        type: 'number',
        hideInput: true,
        category: 'Data',
        helpText:
          "Provide a value to restrict the number of events per batch. It'll divide the total events rendering into batches based on count.",
      },
      dayMaxEvents: {
        label: 'Maximum Events per Day',
        type: 'number',
        category: 'Data',
        helpText:
          'Sets the maximum number of events shown in each day cell. When the number of events exceeds this value, a "+ more" link appears, allowing users to expand and view all events for that day. Accepts a number (e.g., 3) to limit events per cell.',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'fromDateAttribute':
        case 'toDateAttribute':
        case 'titleAttribute':
        case 'allDayAttribute':
          return isNotEmpty(itemData.comboDatasourceAlias);
        case 'hideTimeSlot':
        case 'hideAllDaySlot':
          return !itemData.showOnlyAllDaySlot;
        case 'allDayText':
          return !itemData.hideAllDaySlot;
        default:
          return true;
      }
    },
    onPropertyChange: (property, value) => {
      if (property === 'showOnlyAllDaySlot') {
        callback();
        return {
          [property]: value,
          hideTimeSlot: defaultValue.hideTimeSlot,
          hideAllDaySlot: defaultValue.hideAllDaySlot,
        };
      }
      return {
        [property]: value,
      };
    },
    getDisplayText: (item) => item.props.fromDateAttribute,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'CALENDAR'
        ? { aliasProp: 'comboDatasourceAlias' }
        : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    category: 'Data Containers',
    icon: { iconName: 'calendar' },
    overlayZIndex: 1,
    description: `**Overview**<br/>
The Calendar is a data visualization component that renders records as events on a schedule interface. Instead of listing items in a grid, it places them on a timeline (Month, Week, Day, or List view) based on their start and end dates.
Use this component for Scheduling, Resource Booking, Task Management, or any workflow where "Time" is the primary dimension of the data.

**1. Data Binding (Required)**<br/>
To function, the Calendar acts as a specialized view for a Data Store. You must map your data fields to the calendar's structural requirements.
* **Data Store Alias:** Select the Data Store containing your records.
* **Title Attribute:** The text displayed on the event bar (e.g., "Meeting with Client").
* **From Date / To Date Attributes:** The fields defining when the event starts and ends. (Supported Types: Date, DateTime, StringDateTime).
* **All Day Attribute:** A Boolean field that tells the calendar whether to render the event in the specific hourly slots or in the "All Day" banner at the top.

**2. Views & Navigation**<br/>
You can configure which perspectives are available to the user.
* **Enabled Views:** Toggle Month, Week, Day, and List views based on your use case (e.g., a "Room Booking" app might only need Day and Week views).
* **Navigation Controls:**
    * **Show Year/Month Picker:** Allows rapid jumping to future dates.
    * **Disable Past Navigation:** Prevents users from scrolling back to historical dates (useful for booking systems).
    * **Today Button:** A quick shortcut to return to the current date.



**3. Layout & Density**<br/>
* **Time Slots:**
    * **Hide Time Slot:** Removes the hourly grid (useful if you only have all-day events).
    * **Hide All Day Slot:** Removes the top banner (useful if you only have specific appointments).
* **Density Control:**
    * **Maximum Events per Day:** Sets a limit (e.g., 3) on how many event bars are shown in a single day cell before collapsing them into a "+ X more" link. This prevents the calendar from growing vertically unmanageable.

**4. Interactivity & Logic**<br/>
The Calendar is not just a display; it is an interactive surface. Logic is handled via Triggers added as child components.
* **TriggerOnEventClick:** Fires when a user clicks an existing event. Use this to open a Form or Dialog to edit details.
* **TriggerOnEventCreate:** Fires when a user clicks an empty slot. Use this to open a "New Event" form, pre-filling the clicked date/time.
* **TriggerOnDrop:** Fires when a user drags an event to a new time slot. Use this to update the record's start/end time in the Data Store.
* **TriggerOnDateChange:** Fires when the user navigates to a new month or week (useful for lazy-loading data).`,
    helpRenders: [
      {
        title: 'Sample Calendar',
        description: 'Calendar rendered with differnt type of events',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        height: 800,
        item: {
          children: [],
          itemId: 'calendar',
          parent: 'flexItem',
          props: {
            allDayAttribute: 'allDay',
            comboDatasourceAlias: 'test',
            fromDateAttribute: 'fromDate',
            titleAttribute: 'title',
            toDateAttribute: 'toDate',
            allDayText: 'all-day',
          },
          type: 'Calendar',
        },
      },
    ],
  };
};

export default CalendarManager;
